/**
 * paletteShader.glsl.ts — GLSL ES 3.0 shader source for WebGL palette-swap filter.
 *
 * # What this file owns (Layer 4b, Option B)
 *
 * Two GLSL ES 3.0 shader source strings — one vertex, one fragment —
 * that PixiJS v8.18's `GlProgram.from()` compiles and uploads to the GPU
 * for WebGL rendering.
 *
 *   - This file:    paletteShader.glsl.ts  (GLSL — WebGL backend)
 *   - Sister file:  paletteShader.wgsl.ts  (WGSL — WebGPU backend)
 *
 * PixiJS v8 supports both renderers. Per the v8 Filter constructor source,
 * if a Filter omits the program for the active renderer, the filter is
 * silently skipped — no error, just a missing visual effect. We provide
 * both shader sources so the filter renders correctly under either
 * backend.
 *
 * # GLSL conventions (LOCKED from PixiJS v8.18 source)
 *
 * Verified by reading PixiJS v8.18's own filter sources:
 *
 *   - `defaultFilter.vert.mjs` (vertex template)
 *   - `colorMatrixFilter.frag.mjs` (canonical fragment example)
 *
 * The conventions:
 *
 *   - GLSL ES 3.0 syntax: `in` / `out` declarations (not `attribute` /
 *     `varying`), `texture(sampler, uv)` (not `texture2D`), `finalColor`
 *     output variable (not `gl_FragColor`).
 *
 *   - No `#version 300 es` directive at the top — PixiJS injects this
 *     automatically. Adding it manually causes a duplicate-version error.
 *
 *   - Canonical sampler name: `uTexture` (sampler2D).
 *
 *   - Canonical UV varying: `vTextureCoord` (vec2). Set by the vertex
 *     shader, consumed by the fragment shader.
 *
 *   - PixiJS textures are PRE-MULTIPLIED ALPHA. Color comparisons or
 *     transforms must un-premultiply first, then re-premultiply on output.
 *     This is not optional — skipping the un-premultiply step produces
 *     incorrect results on every semi-transparent pixel (sprite edges,
 *     AA gradients, transparency from indexed-PNG tRNS chunks).
 *
 *   - Default vertex attribute name: `aPosition` (vec2). Declared by the
 *     vertex shader as input.
 *
 *   - Standard PixiJS filter uniforms (provided automatically, do NOT
 *     redeclare in fragment shader): `uInputSize`, `uOutputFrame`,
 *     `uOutputTexture` — used by vertex shader for filter coordinate
 *     transform.
 *
 * # Algorithm — HSL hue rotation (Option B)
 *
 * The AUTeddy "Cyber Marksman" sprite is an indexed PNG with 116 actively-
 * used palette entries clustered into ~5 logical color groups (cream
 * shell, mustard accent, slate mid, charcoal shadow, bright-yellow
 * visor). Each group includes ~15-20 anti-aliased gradient steps baked
 * directly into the indexed palette.
 *
 * An RGB-replacement shader (5 base colors → 5 replacement colors) would
 * only match ~1/15 of the rendered pixels — recoloring saturated peaks
 * of each gradient and leaving AA shoulders original-colored. The result
 * is a patchy, broken sprite.
 *
 * HSL hue-rotation works correctly because it transforms ALL pixels
 * coherently:
 *
 *   1. Sample the texel at the current UV.
 *   2. If alpha is below ε, output transparent and return early.
 *   3. Un-premultiply alpha.
 *   4. Convert RGB → HSL.
 *   5. Apply the four HSL deltas (hueShift radians, satScale, lightShift,
 *      contrast).
 *   6. Convert HSL → RGB.
 *   7. Re-premultiply alpha.
 *   8. Output finalColor.
 *
 * Gradient structure is preserved automatically: cream→tan→darker-tan
 * (an indexed-PNG gradient) becomes red→darker-red→darkest-red after
 * a hue rotation, preserving AA coherence at every step.
 *
 * # Uniform layout
 *
 * The shader receives a single vec4 uniform via UniformGroup binding:
 *
 *     uPaletteTransform = (hueShift_radians, satScale, lightShift, contrast)
 *
 * `hueShift` is converted from degrees to radians at filter-construction
 * time so the shader doesn't pay the trivial conversion cost per-pixel.
 *
 * # Why GLSL ES 3.0 mat3 multiplication is unsafe for HSL conversion
 *
 * A naive RGB→HSL implementation could be expressed as a 3x3 matrix
 * multiply, but HSL conversion is non-linear (involves min/max selection
 * for L, branching for H based on which channel is dominant). A scalar
 * algorithm is correct and idiomatic — same approach PixiJS uses in its
 * own ColorMatrixFilter for non-linear color operations.
 *
 * # References
 *
 *   - GLSL ES 3.0 spec: https://www.khronos.org/registry/OpenGL/specs/es/3.0/GLSL_ES_Specification_3.00.pdf
 *   - HSL/RGB conversion algorithms: standard cgcolorsteam.org / wikipedia
 *   - PixiJS v8.18 source verified: `node_modules/.pnpm/pixi.js@8.18.1/...`
 */

/**
 * Vertex shader (GLSL ES 3.0) — verbatim copy of PixiJS v8.18's default
 * filter vertex shader from `defaultFilter.vert.mjs`.
 *
 * No customization needed; this is the standard coordinate transform
 * that ALL PixiJS v8 filters use unless they have specialized vertex-
 * shader needs (which palette-swap does not — it's a per-pixel color
 * transform, not a geometry transform).
 *
 * Mirrors PixiJS source so future maintainers can confirm it's the
 * unmodified template by diffing against
 * `node_modules/.pnpm/pixi.js@<version>/node_modules/pixi.js/lib/filters/defaults/defaultFilter.vert.mjs`.
 */
export const paletteVertexShader = `in vec2 aPosition;
out vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

vec4 filterVertexPosition( void )
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;

    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0*uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;

    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord( void )
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
}
`;

/**
 * Fragment shader (GLSL ES 3.0) — HSL hue-rotation palette-swap.
 *
 * Reads each texel, converts to HSL, applies the four uniform deltas,
 * converts back to RGB. Handles PixiJS's pre-multiplied alpha correctly
 * (un-premultiply before transform, re-premultiply on output).
 *
 * Uniform input:
 *   - `uTexture`           — the input texture (PixiJS auto-binds this)
 *   - `uPaletteTransform`  — vec4 of (hueShift_radians, satScale,
 *                            lightShift, contrast)
 *
 * Behavior:
 *   - Pixels with alpha < ε pass through transparent (skipped early).
 *   - Pixels with alpha ≥ ε get the full HSL transform.
 *   - Output alpha is preserved from the input texel.
 *
 * The early-out alpha test serves two purposes:
 *   1. Performance: skips per-pixel HSL conversion for transparent
 *      background pixels (~30-50% of the sprite quad's pixels for a
 *      typical character sprite).
 *   2. Correctness: avoids divide-by-near-zero in the un-premultiply
 *      step (color.rgb / color.a) for fully-transparent pixels.
 */
export const paletteFragmentShader = `in vec2 vTextureCoord;

out vec4 finalColor;

uniform sampler2D uTexture;
uniform vec4 uPaletteTransform;

// Convert RGB (0-1 range) to HSL (0-1 range for all components).
// Standard algorithm: L = (max + min) / 2, S derived from L, H derived
// from which channel dominates.
vec3 rgbToHsl(vec3 rgb) {
    float maxC = max(max(rgb.r, rgb.g), rgb.b);
    float minC = min(min(rgb.r, rgb.g), rgb.b);
    float delta = maxC - minC;

    float L = (maxC + minC) * 0.5;

    float S = 0.0;
    if (delta > 0.0) {
        S = (L < 0.5) ? (delta / (maxC + minC)) : (delta / (2.0 - maxC - minC));
    }

    float H = 0.0;
    if (delta > 0.0) {
        if (maxC == rgb.r) {
            H = mod((rgb.g - rgb.b) / delta, 6.0);
        } else if (maxC == rgb.g) {
            H = ((rgb.b - rgb.r) / delta) + 2.0;
        } else {
            H = ((rgb.r - rgb.g) / delta) + 4.0;
        }
        H /= 6.0;
        if (H < 0.0) H += 1.0;
    }

    return vec3(H, S, L);
}

// Helper for hslToRgb: compute one RGB channel from H,S,L parameters.
float hueToRgb(float p, float q, float t) {
    if (t < 0.0) t += 1.0;
    if (t > 1.0) t -= 1.0;
    if (t < 1.0 / 6.0) return p + (q - p) * 6.0 * t;
    if (t < 1.0 / 2.0) return q;
    if (t < 2.0 / 3.0) return p + (q - p) * (2.0 / 3.0 - t) * 6.0;
    return p;
}

// Convert HSL (0-1 range) to RGB (0-1 range).
// Standard inverse algorithm.
vec3 hslToRgb(vec3 hsl) {
    float H = hsl.x;
    float S = hsl.y;
    float L = hsl.z;

    if (S == 0.0) {
        return vec3(L, L, L);
    }

    float q = (L < 0.5) ? L * (1.0 + S) : L + S - L * S;
    float p = 2.0 * L - q;

    float r = hueToRgb(p, q, H + 1.0 / 3.0);
    float g = hueToRgb(p, q, H);
    float b = hueToRgb(p, q, H - 1.0 / 3.0);

    return vec3(r, g, b);
}

void main(void) {
    vec4 color = texture(uTexture, vTextureCoord);

    // Early-out for transparent pixels: skip transform, avoid divide-by-zero.
    if (color.a < 0.001) {
        finalColor = vec4(0.0, 0.0, 0.0, 0.0);
        return;
    }

    // Un-premultiply alpha (PixiJS textures are pre-multiplied alpha).
    vec3 rgb = color.rgb / color.a;

    // RGB -> HSL.
    vec3 hsl = rgbToHsl(rgb);

    // Apply transforms:
    //   uPaletteTransform.x = hueShift in radians (we encode 0-1 hue
    //   space, so convert radians to 0-1 cycle by dividing by 2*PI).
    //   uPaletteTransform.y = satScale
    //   uPaletteTransform.z = lightShift
    //   uPaletteTransform.w = contrast
    float hueShiftCycles = uPaletteTransform.x / (2.0 * 3.14159265358979);
    hsl.x = mod(hsl.x + hueShiftCycles, 1.0);
    hsl.y = clamp(hsl.y * uPaletteTransform.y, 0.0, 1.0);
    hsl.z = clamp((hsl.z - 0.5) * uPaletteTransform.w + 0.5 + uPaletteTransform.z, 0.0, 1.0);

    // HSL -> RGB.
    vec3 newRgb = hslToRgb(hsl);

    // Re-premultiply alpha for output.
    finalColor = vec4(newRgb * color.a, color.a);
}
`;
